/**
 * Panel lateral de detalle (§7).
 *
 * Se abre al hacer clic en una fila de la tabla —y, desde la Fase 5, en un nodo
 * del grafo—. Muestra la ficha completa con sus fuentes enlazadas.
 *
 * Todo el texto que viene del modelo se renderiza como texto plano. No hay
 * `dangerouslySetInnerHTML` en ninguna parte de la app: la salida de Perplexity
 * es entrada no confiable (§8) y React ya escapa por defecto.
 */

import { ExternalLink, Link2, TriangleAlert, X } from 'lucide-react'
import type { ReactNode } from 'react'

import { useStakeholder } from '../api/hooks'
import type { Stakeholder } from '../api/tipos'
import { COLOR_TIPO, ETIQUETA_NIVEL, ETIQUETA_RELACION, ETIQUETA_TIPO } from '../ui/paleta'
import {
  Boton,
  Cargando,
  Chip,
  EscaleraNivel,
  EtiquetaPostura,
  Error as EstadoError,
  MarcaCorroborado,
  MarcaEdicion,
} from '../ui/primitivos'
import { cx, urlExterna } from '../ui/util'

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="border-t border-[var(--color-borde)] px-4 py-3">
      <h3 className="mb-2 text-[11px] font-semibold tracking-wide text-[var(--color-texto-tenue)] uppercase">
        {titulo}
      </h3>
      {children}
    </section>
  )
}

function Lista({ valores }: { valores: string[] }) {
  if (!valores.length) {
    return <p className="text-[12px] text-[var(--color-texto-tenue)]">Sin datos.</p>
  }
  return (
    <ul className="list-disc space-y-1 pl-4 text-[13px] text-[var(--color-texto-suave)]">
      {valores.map((v, i) => (
        <li key={i}>{v}</li>
      ))}
    </ul>
  )
}

function Contenido({ ficha }: { ficha: Stakeholder }) {
  const color = COLOR_TIPO[ficha.tipo] ?? COLOR_TIPO.otro

  return (
    <>
      <div className="px-4 pt-3 pb-3">
        <div className="flex items-start gap-2">
          <span
            aria-hidden
            className="mt-1.5 size-2.5 shrink-0 rounded-full"
            style={{ background: color }}
          />
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] leading-snug font-semibold">{ficha.nombre}</h2>
            <p className="mt-0.5 text-[12px] text-[var(--color-texto-suave)]">
              {ETIQUETA_TIPO[ficha.tipo]}
              {ficha.categoria && ` · ${ficha.categoria}`}
              {ficha.subcategoria && ` · ${ficha.subcategoria}`}
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <EtiquetaPostura postura={ficha.postura} />
          {ficha.estado !== 'ACTIVO' && (
            <Chip color="#8a8f98">
              {ficha.estado === 'DESCARTADO' ? 'Descartado' : 'Fusionado'}
            </Chip>
          )}
          {ficha.editado_por_usuario && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[var(--color-acento)]">
              <MarcaEdicion campos={ficha.campos_editados} /> Editado a mano
            </span>
          )}
          {ficha.corroborado && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[var(--color-favorable)]">
              <MarcaCorroborado corridas={ficha.n_corridas} /> {ficha.n_corridas} corridas
            </span>
          )}
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
          <div>
            <dt className="text-[var(--color-texto-tenue)]">Influencia</dt>
            <dd className="mt-0.5 flex items-center gap-1.5">
              <EscaleraNivel nivel={ficha.nivel_influencia} />
              {ETIQUETA_NIVEL[ficha.nivel_influencia]}
            </dd>
          </div>
          <div>
            <dt className="text-[var(--color-texto-tenue)]">Impacto recibido</dt>
            <dd className="mt-0.5 flex items-center gap-1.5">
              <EscaleraNivel nivel={ficha.nivel_impacto_recibido} />
              {ETIQUETA_NIVEL[ficha.nivel_impacto_recibido]}
            </dd>
          </div>
          {ficha.confianza !== null && (
            <div>
              <dt className="text-[var(--color-texto-tenue)]">Confianza del modelo</dt>
              <dd className="tabular mt-0.5">{Math.round(ficha.confianza * 100)}%</dd>
            </div>
          )}
          {ficha.tipo_relacion_con_entidad && (
            <div>
              <dt className="text-[var(--color-texto-tenue)]">Vínculo con la entidad</dt>
              {/* Las etiquetas ya incluyen su preposición («se opone a»,
                  «colabora con»), así que solo se les añade el complemento. */}
              <dd className="mt-0.5 inline-flex items-center gap-1">
                <Link2 size={12} />
                {ficha.relacion_hacia_entidad
                  ? `${ETIQUETA_RELACION[ficha.tipo_relacion_con_entidad]} la entidad`
                  : `la entidad ${ETIQUETA_RELACION[ficha.tipo_relacion_con_entidad]} este actor`}
              </dd>
            </div>
          )}
        </dl>

        {ficha.alias.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            <span className="text-[11px] text-[var(--color-texto-tenue)]">También:</span>
            {ficha.alias.map((a) => (
              <Chip key={a}>{a}</Chip>
            ))}
          </div>
        )}
      </div>

      {ficha.descripcion && (
        <Seccion titulo="Descripción">
          <p className="text-[13px] leading-relaxed text-[var(--color-texto-suave)]">
            {ficha.descripcion}
          </p>
        </Seccion>
      )}

      {ficha.relacion_con_entidad && (
        <Seccion titulo="Por qué es stakeholder">
          <p className="text-[13px] leading-relaxed text-[var(--color-texto-suave)]">
            {ficha.relacion_con_entidad}
          </p>
        </Seccion>
      )}

      {ficha.intereses.length > 0 && (
        <Seccion titulo="Intereses">
          <Lista valores={ficha.intereses} />
        </Seccion>
      )}

      {ficha.riesgos_o_tensiones.length > 0 && (
        <Seccion titulo="Riesgos o tensiones">
          <Lista valores={ficha.riesgos_o_tensiones} />
        </Seccion>
      )}

      {ficha.mecanismos_de_relacionamiento.length > 0 && (
        <Seccion titulo="Mecanismos de relacionamiento">
          <Lista valores={ficha.mecanismos_de_relacionamiento} />
        </Seccion>
      )}

      {ficha.territorios.length > 0 && (
        <Seccion titulo="Territorios">
          <div className="flex flex-wrap gap-1">
            {ficha.territorios.map((t) => (
              <Chip key={t}>{t}</Chip>
            ))}
          </div>
        </Seccion>
      )}

      {ficha.discrepancias.length > 0 && (
        <Seccion titulo="Discrepancias al fusionar">
          <ul className="space-y-1 text-[12px] text-[var(--color-texto-suave)]">
            {ficha.discrepancias.map((d, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <TriangleAlert
                  size={12}
                  className="mt-0.5 shrink-0 text-[var(--color-critica)]"
                />
                <span>
                  <strong>{d.campo.replace(/_/g, ' ')}</strong>: se conservó «{d.conservado}» y
                  se descartó «{d.descartado}» de otro registro.
                </span>
              </li>
            ))}
          </ul>
        </Seccion>
      )}

      <Seccion titulo={`Fuentes (${ficha.fuentes.length})`}>
        {ficha.fuentes.length === 0 ? (
          <p className="text-[12px] text-[var(--color-texto-tenue)]">
            Esta ficha no tiene fuentes asociadas.
          </p>
        ) : (
          <ul className="space-y-2">
            {ficha.fuentes.map((f) => (
              <li key={f.id} className="text-[12px]">
                {urlExterna(f.url) ? (
                  <a
                    href={urlExterna(f.url)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-start gap-1 font-medium text-[var(--color-acento)] hover:underline"
                  >
                    {f.titulo || f.url}
                    <ExternalLink size={11} className="mt-0.5 shrink-0" />
                  </a>
                ) : (
                  <span className="font-medium">{f.titulo}</span>
                )}
                {(f.publicador || f.fecha) && (
                  <p className="text-[var(--color-texto-tenue)]">
                    {[f.publicador, f.fecha].filter(Boolean).join(' · ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Seccion>

      {Object.values(ficha.identificadores ?? {}).some(Boolean) && (
        <Seccion titulo="Identificadores">
          <dl className="grid gap-1 text-[12px]">
            {Object.entries(ficha.identificadores)
              .filter(([, v]) => v)
              .map(([k, v]) => {
                const enlace = k === 'sitio_web' ? urlExterna(v) : null
                return (
                  <div key={k} className="flex gap-2">
                    <dt className="text-[var(--color-texto-tenue)] capitalize">
                      {k.replace(/_/g, ' ')}
                    </dt>
                    <dd className="min-w-0 break-words">
                      {enlace ? (
                        <a
                          href={enlace}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--color-acento)] hover:underline"
                        >
                          {v}
                        </a>
                      ) : (
                        v
                      )}
                    </dd>
                  </div>
                )
              })}
          </dl>
        </Seccion>
      )}
    </>
  )
}

export function PanelDetalle({
  stakeholderId,
  onCerrar,
  acciones,
}: {
  stakeholderId: number | null
  onCerrar: () => void
  /** Los flujos de curaduría se enchufan en la Fase 6. */
  acciones?: (ficha: Stakeholder) => ReactNode
}) {
  const consulta = useStakeholder(stakeholderId ?? undefined)

  if (stakeholderId === null) return null

  return (
    <aside
      className={cx(
        'aparecer flex w-full shrink-0 flex-col border-l border-[var(--color-borde)]',
        'bg-[var(--color-panel)] sm:w-[380px] lg:w-[420px]',
      )}
      aria-label="Detalle del stakeholder"
    >
      <header className="flex h-10 shrink-0 items-center justify-between border-b border-[var(--color-borde)] px-3">
        <span className="text-[11px] font-semibold tracking-wide text-[var(--color-texto-tenue)] uppercase">
          Detalle
        </span>
        <Boton
          variante="sutil"
          icono={<X size={15} />}
          onClick={onCerrar}
          aria-label="Cerrar"
        />
      </header>

      <div className="scroll-sutil min-h-0 flex-1 overflow-y-auto">
        {consulta.isLoading ? (
          <Cargando />
        ) : consulta.isError ? (
          <EstadoError error={consulta.error} reintentar={() => consulta.refetch()} />
        ) : consulta.data ? (
          <Contenido ficha={consulta.data} />
        ) : null}
      </div>

      {consulta.data && acciones && (
        <footer className="flex shrink-0 flex-wrap gap-1.5 border-t border-[var(--color-borde)] p-2">
          {acciones(consulta.data)}
        </footer>
      )}
    </aside>
  )
}
